// @flow
import {
  BulkEditFormContainer,
  DataEntryHeaderRow,
  DataEntryRow,
  SelectedDataEntriesConsumer,
  // $FlowFixMe - TS file
} from 'general-ledger';
import { colors } from 'bench-style-constants';
import type { ComboBoxOptions } from 'bench-internal-theme';
import React, { Fragment } from 'react';
import styled from 'styled-components';
import { Text, TextBlock } from 'bench-internal-theme';

import type { DataEntry, LedgerAccount } from './types';
import doesDataEntryContainSearchString from './doesDataEntryContainSearchString';

const categorizationColumnHeaders = [
  { heading: 'Date', width: '80px' },
  { heading: 'Transaction Name', width: 'auto' },
  { heading: 'Debit', width: '80px', align: 'right' },
  { heading: 'Credit', width: '80px', align: 'right' },
  { heading: 'Category Suggestions', width: '208px' },
  { heading: 'Notes', width: 'auto', maxWidth: '300px' },
  {
    heading: 'Has client comments, client categorization response, or internal notes',
    icon: 'speechBubble',
  },
  // It doesn't make sense for this column to appear in this view because if this property was true
  // then the data entry wouldn't be in either of the awaiting category ledgers
  // TODO Make the DataEntryRow hide this column if it's not being shown
  { heading: 'Client selected this ledger account', icon: 'circleCheck' },
  { heading: 'Has attachment', icon: 'paperclip' },
  { heading: 'Has flagged internal note(s)', icon: 'flagFilled' },
  { heading: 'Has Add-On', icon: 'link' },
];

const bulkEditFormHeight = 164;

const CategorizationViewContentArea = styled.div`
  bottom: ${({ isBulkEditFormOpen }) => (isBulkEditFormOpen ? `${bulkEditFormHeight}px` : '0')};
  left: 0;
  overflow: auto;
  padding: 32px;
  position: absolute;
  right: 0;
  top: 0;
`;

const BulkEditFormArea = styled.div`
  background: ${colors.pearl};
  bottom: 0;
  box-shadow: 0 -1px ${colors.silt};
  height: ${bulkEditFormHeight}px;
  left: 0;
  overflow: auto;
  position: absolute;
  right: 0;
`;

const CategorizationViewLedgerLayout = styled.div`
  margin: 0 -32px;
  padding: 20px 0 60px;
`;

const LedgerHeader = styled(TextBlock).attrs(() => ({
  styleType: 'mediumBold',
}))`
  padding: 0 32px 8px;
`;

const LedgerDescription = styled(TextBlock).attrs(() => ({
  color: colors.smoke,
  styleType: 'small',
}))`
  max-width: 800px;
  padding: 0 32px 16px;
`;

const DataEntriesCount = styled(Text).attrs(() => ({
  color: colors.smoke,
  styleType: 'mediumBold',
}))`
  margin-left: 8px;
`;

type CategorizationViewProps = {
  awaitingCategoryExpense: LedgerAccount,
  awaitingCategoryRevenue: LedgerAccount,
  ledgerOptions: ComboBoxOptions,
  search: string,
  selectedDataEntry: ?DataEntry,
  toggleDataEntry: DataEntry => any,
};

const CategorizationView = ({
  awaitingCategoryExpense,
  awaitingCategoryRevenue,
  ledgerOptions,
  search,
  selectedDataEntry,
  toggleDataEntry,
}: CategorizationViewProps) => {
  const isSearchStringSet = search.trim() !== '';
  const ledgerAccounts = [awaitingCategoryRevenue, awaitingCategoryExpense];
  const filteredDataEntriesGroupedByLedgerId = {};
  let availableDataEntries = [];
  ledgerAccounts.forEach(ledgerAccount => {
    const shownDataEntries = ledgerAccount.dataEntries.filter(dataEntry =>
      isSearchStringSet ? doesDataEntryContainSearchString(dataEntry, search) : true,
    );
    filteredDataEntriesGroupedByLedgerId[ledgerAccount.id] = shownDataEntries;
    availableDataEntries = availableDataEntries.concat(shownDataEntries);
  });
  return (
    <SelectedDataEntriesConsumer>
      {({ selectedDataEntryIds }: any) => {
        const areAnyDataEntriesSelectedForBulkEdit = !!selectedDataEntryIds.length;
        return (
          <Fragment>
            <CategorizationViewContentArea
              isBulkEditFormOpen={areAnyDataEntriesSelectedForBulkEdit}
            >
              <TextBlock styleType="title">Categorization</TextBlock>
              {[awaitingCategoryRevenue, awaitingCategoryExpense].map(ledgerAccount =>
                ledgerAccount.dataEntries.length === 0 ? null : (
                  <CategorizationViewLedgerLayout key={ledgerAccount.id}>
                    <LedgerHeader>
                      {ledgerAccount.name}
                      <DataEntriesCount>({ledgerAccount.dataEntries.length})</DataEntriesCount>
                    </LedgerHeader>
                    <LedgerDescription>
                      Review all newly posted transactions that have been categorized to the{' '}
                      {ledgerAccount.name} ledger before asking the client for categorization input.
                    </LedgerDescription>
                    <DataEntryHeaderRow
                      columns={categorizationColumnHeaders}
                      dataEntriesUnderHeader={
                        filteredDataEntriesGroupedByLedgerId[ledgerAccount.id]
                      }
                      ledgerAccount={ledgerAccount}
                    />
                    {filteredDataEntriesGroupedByLedgerId[ledgerAccount.id].map(
                      (dataEntry, index) => (
                        <DataEntryRow
                          key={dataEntry.id}
                          hasBackgroundColor={index % 2}
                          dataEntry={dataEntry}
                          isSelected={!!selectedDataEntry && selectedDataEntry.id === dataEntry.id}
                          ledgerOptions={ledgerOptions}
                          search={search}
                          showCategorizationSuggestions
                          toggleDataEntry={toggleDataEntry}
                        />
                      ),
                    )}
                  </CategorizationViewLedgerLayout>
                ),
              )}
            </CategorizationViewContentArea>
            {areAnyDataEntriesSelectedForBulkEdit && (
              <BulkEditFormArea>
                <BulkEditFormContainer availableDataEntries={availableDataEntries} />
              </BulkEditFormArea>
            )}
          </Fragment>
        );
      }}
    </SelectedDataEntriesConsumer>
  );
};

export default CategorizationView;
